import { Resource,ResourceLoadContext } from "./resource";

export interface TextureAsset {
  texture: GPUTexture;
  view: GPUTextureView;
  sampler: GPUSampler;
  width: number;
  height: number;
  format: GPUTextureFormat;
}

export class TextureResource extends Resource<TextureAsset> {
  async load(ctx: ResourceLoadContext): Promise<TextureAsset> {
    const response = await ctx.fetchPrivate(this.key);
    if (!response.ok) {
      throw new Error(`Failed to load texture "${this.key}": ${response.status}`);
    }

    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);

    const format: GPUTextureFormat = "rgba8unorm";

    const texture = ctx.device.createTexture({
      label: `TextureResource:${this.key}`,
      size: {
        width: bitmap.width,
        height: bitmap.height,
        depthOrArrayLayers: 1,
      },
      format,
      mipLevelCount: 1,
      sampleCount: 1,
      dimension: "2d",
      usage:
        GPUTextureUsage.TEXTURE_BINDING |
        GPUTextureUsage.COPY_DST |
        GPUTextureUsage.RENDER_ATTACHMENT,
    });

    ctx.device.queue.copyExternalImageToTexture(
      { source: bitmap },
      { texture },
      { width: bitmap.width, height: bitmap.height }
    );

    const view = texture.createView({
      label: `TextureResourceView:${this.key}`,
    });

    const sampler = ctx.device.createSampler({
      label: `TextureResourceSampler:${this.key}`,
      magFilter: "linear",
      minFilter: "linear",
      mipmapFilter: "linear",
      addressModeU: "repeat",
      addressModeV: "repeat",
    });

    this._value = {
      texture,
      view,
      sampler,
      width: bitmap.width,
      height: bitmap.height,
      format,
    };

    return this._value;
  }

  dispose(_ctx: ResourceLoadContext): void {
    if (this._value) {
      this._value.texture.destroy();
      this._value = undefined;
    }
  }
}
